<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef } from "vue";
import dayjs from "dayjs";
import type { VForm } from "vuetify/components";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import FileUploadingGrid from "@/views/trips/components/FileUploadingGrid.vue";
import FreightDetails from "@/views/trips/view/components/FreightDetails.vue";
import { API } from "@/api/api";
import { useBaseView, useDateTime } from "@/composables";
import { useError } from "@/composables/useError";
import { useValidation } from "@/composables/useValidation";
import { OrderStopType } from "@/constants/order";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import type { StopInfoFormProps } from "@/types/props";
import type { DeliveryInfoFormData, StopInfoFile } from "@/types/trip";

const loading = defineModel<boolean>("loading", { default: false });

const props = withDefaults(defineProps<StopInfoFormProps>(), {
  preload: false,
  readonly: false,
});

const emits = defineEmits<{
  (e: "save"): void;
}>();

const api = new API();

const { dateToUTC, timezonesShortList } = useDateTime();
const { getPreviewPath } = useBaseView();
const { requiredValidator } = useValidation();
const { showAPIError, scrollToError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const unloadData = ref<DeliveryInfoFormData>({
  date: "",
  time: "",
  timezone: "",
  signedBy: "",
  podFiles: [],
  files: [],
  freights: [],
});

const routeStops = computed<RouteStopDto[]>(() => {
  console.log("route", props.route);
  return props.route.routeStops;
});

const pickUps = computed(() => routeStops.value.filter((stop) => stop.type === OrderStopType.pickup));

const stopFreights = computed(() => props.route.freights.filter((freight) => props.stop.freights.includes(freight.id)));

async function init(): Promise<void> {
  unloadData.value.freights = [...stopFreights.value];

  if (!props.preload) {
    unloadData.value.timezone = props.stop.facility?.timezone ?? "";
    const date = dayjs().tz(unloadData.value.timezone);
    unloadData.value.date = date.format("YYYY-MM-DD");
    unloadData.value.time = date.format("HH:mm");

    return;
  }

  unloadData.value.signedBy = props.stop.podSignedBy ?? "";
  unloadData.value.timezone = props.stop.timezone ?? "";
  const date = dayjs(props.stop.loadUnloadTime).tz(unloadData.value.timezone);
  unloadData.value.date = date.format("YYYY-MM-DD");
  unloadData.value.time = date.format("HH:mm");

  try {
    const response = await api.trips.getTripFiles(props.tripId);

    if (response.success && response.data) {
      const stopFiles = response.data
        .filter((f) => f.tags?.includes("route_stop:" + props.stop.id))
        .map((f) => ({
          ...f,
          previewPath: getPreviewPath(f),
        }));

      unloadData.value.podFiles = stopFiles.filter((f) => f.tags?.includes("pod"));
      unloadData.value.files = stopFiles.filter((f) => f.tags?.includes("other"));
    }
  } catch (error) {
    console.error(error);
  }
}

async function uploadFiles(files: StopInfoFile[], fileType: string, withPreviewPath: boolean): Promise<void> {
  const uploadPromises = files.map(async (uploadingFile) => {
    uploadingFile.loading = true;

    try {
      const response = await api.trips.uploadRouteStopFile(
        props.tripId,
        String(props.stop.id),
        fileType,
        uploadingFile.file!,
        "dummy",
      );

      if (response.success && response.data) {
        Object.assign(
          uploadingFile,
          withPreviewPath ? { ...response.data, previewPath: getPreviewPath(response.data) } : response.data,
        );
      } else {
        uploadingFile.error = true;
      }
    } catch (error) {
      console.error(error);
      uploadingFile.error = true;
    } finally {
      uploadingFile.loading = false;
    }
  });

  await Promise.all(uploadPromises);
}

async function save(): Promise<void> {
  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError(formRef.value?.$el);

    return;
  }

  loading.value = true;

  await Promise.all([
    uploadFiles(unloadData.value.podFiles, "pod", true),
    uploadFiles(unloadData.value.files, "other", false),
  ]);

  try {
    const response = await api.trips.unloadRouteStop(props.tripId, String(props.stop.id), {
      unloadTime: dateToUTC(unloadData.value.date + " " + unloadData.value.time, unloadData.value.timezone),
      podFiles: unloadData.value.podFiles,
      otherFiles: unloadData.value.files,
      podSignedBy: unloadData.value.signedBy,
      freights: unloadData.value.freights,
    });

    if (response.success) {
      emits("save");
    } else {
      showAPIError(response, "Error unload verification at stop");
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  init();
});

defineExpose({ save });
</script>

<template>
  <v-form ref="form">
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-6">POD info:</div>
      <div class="text-grey-darken-1">Freight(s):</div>

      <template v-for="(item, index) in pickUps" :key="item.id">
        <freight-details
          v-model="unloadData.freights"
          :disabled="readonly"
          :index="index"
          :pick-up="item"
          :route-stops="routeStops"
        />
      </template>

      <v-text-field
        v-model="unloadData.signedBy"
        :disabled="readonly"
        :rules="[requiredValidator]"
        autocomplete="off"
        class="required mt-6"
        color="primary"
        data-qa="signed-by-field"
        density="compact"
        hide-details="auto"
        label="Signed by"
        variant="outlined"
      />
    </div>
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Attached files</div>
      <div class="text-grey-darken-1 font-weight-500 mb-4">POD files<span class="text-error">*</span></div>
      <file-uploading-grid
        v-model="unloadData.podFiles"
        :columns="3"
        :disabled="readonly"
        required
      ></file-uploading-grid>
      <div class="text-grey-darken-1 font-weight-500 mb-4 mt-6">Other files</div>
      <file-uploading-grid v-model="unloadData.files" :columns="3" :disabled="readonly"></file-uploading-grid>
    </div>
    <div>
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Time</div>
      <v-row>
        <v-col cols="3">
          <timezone-select
            v-model="unloadData.timezone"
            :disabled="readonly"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="6">
          <date-picker v-model="unloadData.date" :disabled="readonly" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="unloadData.time" :disabled="readonly" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<style lang="scss" scoped></style>
